import { FormikHelpers } from "formik";
import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { IEditableSectionRenderProps } from "@/components/EditableSection";
import { applyApiErrors } from "@/components/Formik";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_XS } from "@/core/media";
import { changePassword } from "@/data/users";
import { Flex, Group, MessagesContext } from "@/eworkitComponents";
import { useToasts } from "@/views/components/Toast/useToasts";

import { IPasswordFormValues, PasswordFormik, PASSWORD_FORM_MESSENGER_CONTEXT } from "./PasswordForm";

interface IPasswordSectionProps extends IEditableSectionRenderProps {}

export const PasswordSection: FC<IPasswordSectionProps> = ({ editableProps }) => {
  const { t } = useTranslation(["session", "common"]);
  const { addMessage } = useContext(MessagesContext);
  const { addToast } = useToasts();

  const isVerySmallScreen = useMedia(MEDIA_SIZE_XS);

  const handleSubmit = useCallback(
    async (values: IPasswordFormValues, actions: FormikHelpers<IPasswordFormValues>) => {
      try {
        await changePassword(values);
        actions.setSubmitting(false);
        editableProps.handleCancelClick();
        addToast({ text: t("USERS.SETTINGS.USER_ACCOUNT.SUCCESS_MESSAGE"), type: "success" });
      } catch (error) {
        if (error.payload?.code && error.payload.code === "INVALID_PASSWORD") {
          actions.setFieldError("oldPassword", error.payload.code);
        } else if (error.payload?.code && error.payload.code === "NOT_VALID_PARAM") {
          applyApiErrors(actions, error.payload);
        } else {
          addMessage({
            text: t("common:VALIDATION_MESSAGES.GENERAL"),
            type: "warning",
            context: PASSWORD_FORM_MESSENGER_CONTEXT,
          });
          applyApiErrors(actions, error.payload);
        }
        actions.setSubmitting(false);
      }
    },
    [changePassword, addMessage, addToast],
  );

  return editableProps.editMode ? (
    <PasswordFormik
      editableProps={editableProps}
      initialValues={{ newPassword: "", confirmNewPassword: "", oldPassword: "" }}
      onSubmit={handleSubmit}
    />
  ) : (
    <Flex direction={isVerySmallScreen ? "column" : "row"} innerMargin="md">
      <Flex direction="column" width={{ xxs: 12, md: 4 }}>
        <Group label={t("USERS.SETTINGS.USER_ACCOUNT.PASSWORD.PASSWORD")} direction="column">
          <Text size="md" color="black">
            ••••••••••
          </Text>
        </Group>
      </Flex>
    </Flex>
  );
};
